/**
 * billing.limits.* API (plan billing-hardening Stage 6, INV-BH-3/7) — the
 * caller's credit limit, lifetime spend and entitlement status, plus the
 * admin write. Same WS-RPC + react-query pattern as useSourceAppConfig.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useRpcMutation, useRpcQuery } from "../../../runtime/hooks/useRpc";

export interface BillingLimitInfo {
  readonly user_id: string;
  /** Lifetime credit limit in micro-dollars; null = no limit configured. */
  readonly credit_limit_micros: number | null;
  readonly spent_micros: number;
  readonly reserved_micros: number;
  /** Remaining live capacity after committed spend and active reservations. */
  readonly availableMicros: number | null;
  /** Mirrors the turn gate: false = next turn would be refused. */
  readonly entitled: boolean;
}

export interface UseBillingLimitResult {
  readonly limit: BillingLimitInfo | undefined;
  readonly loading: boolean;
  readonly error: string | null;
  readonly saving: boolean;
  readonly saveError: string | null;
  /** Set (micros) or clear (null) the caller's credit limit. Admin-only. */
  readonly setLimit: (creditLimitMicros: number | null) => void;
  /** Re-run the billing.limits.get query (retry after a transport error). */
  readonly reload: () => void;
}

const LIMIT_KEY = ["settings", "billing-limit"] as const;

export function useBillingLimit(): UseBillingLimitResult {
  const queryClient = useQueryClient();
  const query = useRpcQuery<BillingLimitInfo>(LIMIT_KEY, "billing.limits.get", undefined, {
    retry: false,
  });
  const mutation = useRpcMutation<BillingLimitInfo, { credit_limit_micros: number | null }>(
    "billing.limits.set",
    {
      onSuccess: (): void => {
        void queryClient.invalidateQueries({ queryKey: LIMIT_KEY });
      },
    },
  );

  return {
    limit: query.data,
    loading: query.isLoading,
    error: query.error ? query.error.message : null,
    saving: mutation.isPending,
    saveError: mutation.error ? mutation.error.message : null,
    setLimit: (creditLimitMicros: number | null): void => {
      mutation.mutate({ credit_limit_micros: creditLimitMicros });
    },
    reload: (): void => {
      void query.refetch();
    },
  };
}
